import { Axios, setAuthToken } from "./config/Axios";
import { loadAgencyFromApi } from "./utils/helper";

////// AUTHENTICATION //////
// Login User
export const loginUser = async (params) => {
  setAuthToken();
  const response = await Axios.post("/api/auth/login", params);
  return response;
};

export const getUserInfo = async (params) => {
  setAuthToken();
  const response = await Axios.post("/api/auth/get-user-info", params);
  return response;
};

// Microsoft (Azure AD) login — exchanges the MS Graph access token for an app session token
export const microsoftLogin = async (params) => {
  setAuthToken();
  const response = await Axios.post("/api/auth/microsoft", params);
  return response;
};

//update password
export const updatePassword = async (params) => {
  setAuthToken();
  const response = await Axios.patch(
    "/api/super-admin/change-admin-password",
    params,
  );
  return response;
};

//change password for agency admin & users
export const changePassword = async (params) => {
  setAuthToken();
  const response = await Axios.patch("/api/user/change-password", params);
  return response;
};

//set new password
export const setNewPassword = async (params) => {
  setAuthToken();
  const response = await Axios.patch("/api/user/update-password", params);
  return response;
};

// forgot password — sends reset link to email
export const forgotPassword = async (params) => {
  setAuthToken();
  const response = await Axios.post("/api/auth/forgot-password", params);
  return response;
};

////// SSO ONBOARDING (pending new users) //////
export const getSsoAgencyList = async (token) => {
  setAuthToken(token);
  const response = await Axios.get("/api/user/get-agency-list");
  return response;
};

export const getSsoAgencyAdmins = async (agencyId, token) => {
  setAuthToken(token);
  const response = await Axios.get(
    `/api/user/get-admins-of-agency/${agencyId}`,
  );
  return response;
};

export const sendRequestToAdmin = async (params, token) => {
  setAuthToken(token);
  const response = await Axios.post("/api/user/send-request-to-admin", params);
  return response;
};

//set agency
export const setAgency = async (agencyId) => {
  setAuthToken();
  const response = await Axios.patch(`/api/super-admin/set-agency/${agencyId}`);
  return response;
};

//////DASHBOARD////////
// Get Dashboard Stats
export const getDashboardData = async () => {
  setAuthToken();
  const response = await Axios.get("/api/dashboard/get-dashboard-stats");
  return response;
};

//Get simulation stats
export const getSimulationStats = async () => {
  setAuthToken();
  const response = await Axios.get("/api/dashboard/get-ai-simulation-stats");
  return response;
};

export const getAgencyStats = async () => {
  setAuthToken();
  const response = await Axios.get("/api/admin/get-stats");
  return response;
};

///// AGENCY ////
// Create Agency
export const createAgency = async (params) => {
  setAuthToken();
  const response = await Axios.post("/api/agency/create", params);
  await loadAgencyFromApi();
  return response;
};

// Get All Agencies
export const getAllAgencies = async (params) => {
  setAuthToken();
  const response = await Axios.get("/api/agency", { params });
  return response;
};

// Update Agency
export const updateAgency = async (params) => {
  setAuthToken();
  const response = await Axios.put(`/api/agency`, params);
  await loadAgencyFromApi();
  return response;
};

// Agency admin self-service: update own current/next fiscal year PO numbers
export const updateOwnPoNumbers = async (params) => {
  setAuthToken();
  const response = await Axios.post(`/api/agency/po-numbers`, params);
  return response;
};

// Agency admin self-service: stored PO numbers and whether the agency is paused
// for a missing PO (works without an active contract)
export const getOwnPoStatus = async () => {
  setAuthToken();
  const response = await Axios.get(`/api/agency/po-status`);
  return response;
};

// Delete Agency
export const deleteAgency = async (id) => {
  setAuthToken();
  const response = await Axios.delete(`/api/agency`, { data: { id } });
  await loadAgencyFromApi();
  return response;
};

// Delete Agency (soft delete since INV-10: marked deleted and deactivated; name, contracts and history kept)
export const deleteAgencyPermanent = async (id) => {
  setAuthToken();
  const response = await Axios.delete(`/api/agency/permanent`, { data: { id } });
  await loadAgencyFromApi();
  return response;
};

//Activate Agency
export const activateAgency = async (id) => {
  setAuthToken();
  const response = await Axios.patch("/api/agency/activate", { id });
  return response;
};

///// BRAND ////
// Get All Brands
export const getAllBrands = async (params) => {
  setAuthToken();
  const response = await Axios.get("/api/brands", { params });
  return response;
};

// Create Brand
export const createBrand = async (params) => {
  setAuthToken();
  const response = await Axios.post("/api/brands/create", params);
  return response;
};

// Update Brand (id, name)
export const updateBrand = async (params) => {
  setAuthToken();
  const response = await Axios.put("/api/brands", { id: params.id, name: params.name });
  return response;
};

// Delete Brand
export const deleteBrand = async (id) => {
  setAuthToken();
  const response = await Axios.delete(`/api/brands`, { data: { id } });
  return response;
};

// Permanently Delete Brand (hard delete, cascades to its projects/simulations)
export const deleteBrandPermanent = async (id) => {
  setAuthToken();
  const response = await Axios.delete(`/api/brands/permanent`, { data: { id } });
  return response;
};

//Activate Brand
export const activateBrand = async (id) => {
  setAuthToken();
  const response = await Axios.patch("/api/brands/activate", { id });
  return response;
};

// Project///
// Get All Projects (params: page, perPage)
export const getAllProjects = async (params) => {
  setAuthToken();
  const response = await Axios.get("/api/project", { params });
  return response;
};

// Create Project (form-data: name, brand_id, country, persona file, simulation_description, simulation_funnel_stage, competitor_brands)
export const createProject = async (params) => {
  setAuthToken();
  const formData = new FormData();
  formData.append("name", params.name ?? "");
  formData.append("brand_id", String(params.brand_id ?? ""));
  formData.append("country", params.country ?? "");
  formData.append(
    "simulation_description",
    params.simulation_description ?? "",
  );
  formData.append(
    "simulation_funnel_stage",
    params.simulation_funnel_stage ?? "",
  );
  const competitorBrands = Array.isArray(params.competitor_brands)
    ? params.competitor_brands
    : [];
  formData.append("competitor_brands", JSON.stringify(competitorBrands));
  if (params.persona && params.persona instanceof File) {
    formData.append("persona", params.persona);
  }
  const response = await Axios.post("/api/project/create", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return response;
};

// Update Project (id, name, country, brand_id)
export const updateProject = async (params) => {
  setAuthToken();
  const payload = {
    id: params.id,
    name: params.name ?? "",
    country: params.country ?? "",
    brand_id: params.brand_id,
  };
  const response = await Axios.put("/api/project", payload);
  return response;
};

// Delete Project
export const deleteProject = async (id) => {
  setAuthToken();
  const response = await Axios.delete(`/api/project`, { data: { id } });
  return response;
};

// Get Project by ID
export const getProjectDetails = async (params) => {
  setAuthToken();
  const response = await Axios.get(`/api/project/details`, { params });
  return response;
};

//Get Project Stats
export const getProjectStats = async () => {
  setAuthToken();
  const response = await Axios.get(`/api/project/stats`);
  return response;
};

///// ADMIN (Invite & Manage) ////
// Get All Admins (params: page, perPage). Response: { data: [], pagination: {} }
export const getAllAdmins = async (params = {}) => {
  setAuthToken();
  const response = await Axios.get("/api/admin", { params });
  return response;
};

// Create Admin (body: full_name, email, password)
export const createAdmin = async (body) => {
  setAuthToken();
  const response = await Axios.post("/api/admin/create", body);
  return response;
};

// Update Admin (body: id, full_name)
export const updateAdmin = async (body) => {
  setAuthToken();
  const response = await Axios.put("/api/admin", body);
  return response;
};

// Delete Admin (body: id)
export const deleteAdmin = async (id) => {
  setAuthToken();
  const response = await Axios.delete("/api/admin", { data: { id } });
  return response;
};

// Permanently Delete Admin (hard delete, cascades to their simulations)
export const deleteAdminPermanent = async (id) => {
  setAuthToken();
  const response = await Axios.delete("/api/admin/permanent", { data: { id } });
  return response;
};

//activate admin
export const activateAdmin = async (id) => {
  setAuthToken();
  const response = await Axios.patch("/api/admin/activate", { id });
  return response;
};

///SIMULATION///
// Get All Simulations (params: page, limit)
export const getAllSimulations = async (params) => {
  setAuthToken();
  const response = await Axios.get("/api/simulation", { params });
  return response;
};

//Delete Simulation
export const deleteAiSimulation = async (id) => {
  setAuthToken();
  const response = await Axios.delete("/api/simulation", { data: { id } });
  return response;
};

//Get Simulation Details (optional showTranslation matches getOneSimulationDetails)
export const getAiSimulationDetails = async (id, showTranslation) => {
  setAuthToken();
  const params = { id };
  if (showTranslation === true || showTranslation === false) {
    params.showTranslation = showTranslation;
  }
  const response = await Axios.get(`/api/simulation/details`, { params });
  return response;
};

// Cross-Platform Analysis tab data (latest run's results.cross_platform)
export const getCrossPlatformAnalysis = async (simulationId) => {
  setAuthToken();
  const response = await Axios.get(`/api/simulation/cross-platform`, {
    params: { simulation_id: simulationId },
  });
  return response;
};

//Trigger Simulation
export const createAiSimulation = async (params) => {
  setAuthToken();
  const response = await Axios.post("/api/simulation/trigger", params);
  return response;
};

//generate queries
export const generateQueries = async (params) => {
  setAuthToken();
  const response = await Axios.post("/api/simulation/generate-queries", params);
  return response;
};

//dwld pdf – returns PDF blob
export const downloadPdf = async (simulationId, runId = null, showTranslation = false) => {
  setAuthToken();
  const params = {};
  if (runId != null) params.run_id = runId;
  if (showTranslation) params.showTranslation = true;
  const response = await Axios.get(
    `api/pdf/generate-simulation-report/${simulationId}`,
    {
      responseType: "blob",
      params,
    },
  );
  return response;
};

//fetch export history (params: page, perPage)
export const fetchExportReportData = async (params = {}) => {
  setAuthToken();
  const { page = 1, limit = 5, simulation_id } = params;
  const response = await Axios.get("/api/pdf", {
    params: { page, limit, simulation_id },
  });
  return response;
};

//////////USER MANAGEMENT BY SUPER ADMIN//////////

//get all users by agency id
export const getAllUsers = async (params = {}, agencyId = null) => {
  setAuthToken();
  const response = await Axios.get(
    `/api/super-admin/user/get-all/${agencyId}`,
    { params },
  );
  return response;
};

//create user
export const createUser = async (params = {}) => {
  setAuthToken();
  const response = await Axios.post("/api/super-admin/user/create", params);
  return response;
};

/**
 * Update user by id.
 * @param {Object} params - Request body: { full_name, role, password } (password optional when not changing)
 * @param {string} [userId] - User id to update
 */
export const updateUser = async (params = {}, userId = null) => {
  setAuthToken();
  const body = {
    full_name: params.full_name,
    role: params.role,
    password: params.password || undefined,
  };
  const response = await Axios.put(
    `/api/super-admin/user/update/${userId}`,
    body,
  );
  return response;
};

export const switchUserAgency = async (params = {}, userId = null) => {
  setAuthToken();
  const response = await Axios.patch(
    `/api/super-admin/user/switch-agency/${userId}`,
    {
      agency_id: Number(params.agency_id),
      full_name: params.full_name || undefined,
      role: params.role || undefined,
      replacement_admin_id: params.replacement_admin_id
        ? Number(params.replacement_admin_id)
        : undefined,
    },
  );
  return response;
};

export const checkSwitchAgency = async (userId = null) => {
  setAuthToken();
  const response = await Axios.get(
    `/api/super-admin/user/switch-agency-check/${userId}`,
  );
  return response;
};

export const getUserById = async (userId = null) => {
  setAuthToken();
  const response = await Axios.get(`/api/super-admin/user/${userId}`);
  return response;
};

//activate user
export const activateUser = async (userId = null) => {
  setAuthToken();
  const response = await Axios.patch(
    `/api/super-admin/user/activate/${userId}`,
  );
  return response;
};

//deactivate user
export const deactivateUser = async (userId = null) => {
  setAuthToken();
  const response = await Axios.patch(
    `/api/super-admin/user/deactivate/${userId}`,
  );
  return response;
};

// Permanently Delete User (hard delete, cascades to their simulations)
export const deleteUserPermanent = async (userId = null) => {
  setAuthToken();
  const response = await Axios.delete(
    `/api/super-admin/user/${userId}/permanent`,
  );
  return response;
};

// ////////////// BREAD CRUMBS ?????????
//agency list
export const getAgencyList = async () => {
  setAuthToken();
  const response = await Axios.get("/api/agency/get-all");
  return response;
};

//brands list
export const getBrandList = async () => {
  setAuthToken();
  const response = await Axios.get("/api/brands/all");
  return response;
};

//projects list
export const getProjectsList = async (params) => {
  setAuthToken();
  const response = await Axios.get("/api/project/all", { params });
  return response;
};

//simulation list
export const getSimulationsList = async (params) => {
  setAuthToken();
  const response = await Axios.get("/api/simulation/all", { params });
  return response;
};

///// PROJECT Phase 2 ////
//create project
export const createProject_ = async (params) => {
  setAuthToken();
  const response = await Axios.post("/api/project/create", params);
  return response;
};

//get all projects
export const getAllProjects_ = async (params) => {
  setAuthToken();
  const response = await Axios.get("/api/project", { params });
  return response;
};

//edit project
export const editProject_ = async (params, id) => {
  setAuthToken();
  const response = await Axios.put(`/api/project/${id}`, params);
  return response;
};

//activate project
export const activateProject_ = async (id) => {
  setAuthToken();
  const response = await Axios.patch(`/api/project/activate/${id}`);
  return response;
};

//delete project
export const deleteProject_ = async (id) => {
  setAuthToken();
  const response = await Axios.delete(`/api/project/${id}`);
  return response;
};

// Permanently Delete Project (hard delete, cascades to its simulations)
export const deleteProjectPermanent_ = async (id) => {
  setAuthToken();
  const response = await Axios.delete(`/api/project/${id}/permanent`);
  return response;
};

//Simulations Phase 2 — multipart: project_id, persona (.json), name, description, language,
// llm_platform, competitor_brands, funnel_stage
//parse persona file
export const parseJsonFile = async (params) => {
  setAuthToken();
  const formData = new FormData();
  if (params.project_id != null && params.project_id !== "") {
    formData.append("project_id", String(params.project_id));
  }
  if (params.persona instanceof File) {
    formData.append("persona", params.persona);
  }
  if (params.name != null) {
    formData.append("name", String(params.name));
  }
  if (params.description != null) {
    formData.append("description", String(params.description));
  }
  if (params.language != null && params.language !== "") {
    formData.append("language", String(params.language));
  }
  const llmPlatforms = params.llm_platforms;
  if (Array.isArray(llmPlatforms) && llmPlatforms.length > 0) {
    formData.append("llm_platform", String(llmPlatforms[0]));
    formData.append("llm_platforms", JSON.stringify(llmPlatforms));
  } else if (params.llm_platform != null && params.llm_platform !== "") {
    formData.append("llm_platform", String(params.llm_platform));
  }
  if (params.competitor_brands != null) {
    const competitorBrands = Array.isArray(params.competitor_brands)
      ? params.competitor_brands
      : [];
    formData.append("competitor_brands", JSON.stringify(competitorBrands));
  }
  if (params.funnel_stage != null && params.funnel_stage !== "") {
    formData.append("funnel_stage", String(params.funnel_stage));
  }
  const response = await Axios.post("/api/simulation/parse-json", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return response;
};

//get simulations
export const getSimulations = async (params) => {
  setAuthToken();
  const response = await Axios.get("/api/simulation", { params });
  return response;
};

// Generate queries / save simulation (JSON body: simulation_body, selected_audience e.g. "audience_1")
export const saveSimulation = async (params) => {
  setAuthToken();
  const response = await Axios.post("/api/simulation/generate-queries", params);
  return response;
};

//get one simulation details
export const getOneSimulationDetails = async (id, showTranslation) => {
  setAuthToken();
  const response = await Axios.get(`/api/simulation/details`, {
    params: { id, showTranslation },
  });
  return response;
};

//get all runs
export const getAllRuns = async (params) => {
  setAuthToken();
  const response = await Axios.get(`/api/simulation/runs`, { params });
  return response;
};

//get a single run's full details
export const getRunById = async (runId, showTranslation) => {
  setAuthToken();
  const params = {};
  if (showTranslation === true || showTranslation === false) {
    params.showTranslation = showTranslation;
  }
  const response = await Axios.get(`/api/simulation/runs/${runId}`, { params });
  return response;
};

// Re-run simulation (JSON body: { simulation_id })
export const reRunSimulation = async (params) => {
  setAuthToken();
  const response = await Axios.post(
    `/api/simulation/recreate-simulation`,
    params,
  );
  return response;
};

//delete simulation
export const deleteSimulation = async (id) => {
  setAuthToken();
  const response = await Axios.delete(`/api/simulation`, { data: { id } });
  return response;
};

// Permanently Delete Simulation (hard delete, including its runs/queries/outputs)
export const deleteSimulationPermanent = async (id) => {
  setAuthToken();
  const response = await Axios.delete(`/api/simulation/permanent`, { data: { id } });
  return response;
};

// retry a failed simulation
export const retrySimulation = async (id) => {
  setAuthToken();
  const response = await Axios.post(`/api/simulation/retry/${id}`);
  return response;
};
//req body is { id}
export const activateSimulation = async (params) => {
  setAuthToken();
  const response = await Axios.patch(`/api/simulation/activate`, params);
  return response;
};

//edit simulation
export const editSimulation = async (params) => {
  setAuthToken();
  const response = await Axios.patch(`/api/simulation`, params);
  return response;
};

//get persona from library
export const getPersonaFromLibrary = async () => {
  setAuthToken();
  const response = await Axios.get(`/api/simulation/get-persona/all`);
  return response;
};

//runs tab
//compare simulation over time
export const compareSimulationOverTime = async (simulationId) => {
  setAuthToken();
  const response = await Axios.get(
    `/api/simulation-runs/compare/${simulationId}`,
  );
  return response;
};

//trigger re run
export const triggerReRun = async (simulationId) => {
  setAuthToken();
  const response = await Axios.get(
    `/api/simulation-runs/trigger/${simulationId}`,
  );
  return response;
};

//schedule run
export const scheduleRun = async (params) => {
  setAuthToken();
  const response = await Axios.post(`/api/simulation-runs/schedule`, params);
  return response;
};

//remove schedule
export const removeSchedule = async (simulationId) => {
  setAuthToken();
  const response = await Axios.patch(
    `/api/simulation-runs/stop/${simulationId}`,
  );
  return response;
};

//stats
export const get_project_stats = async (params) => {
  setAuthToken();
  const response = await Axios.get(`/api/dashboard/get-project-stats`, {
    params,
  });
  return response;
};

export const get_simulation_stats = async (params) => {
  setAuthToken();
  const response = await Axios.get(`/api/dashboard/get-simulations-stats-v2`, {
    params,
  });
  return response;
};

//translation
export const getTranslation = async (id) => {
  setAuthToken();
  const response = await Axios.get(`/api/simulation/translate/${id}`);
  return response;
};

//cost api
export const getCost = async (params) => {
  setAuthToken();
  const response = await Axios.get(`/api/cost-tokens/overview`, { params });
  return response;
};

//generate persona from ai
export const generatePersonaFromAi = async (params) => {
  setAuthToken();
  const response = await Axios.post(
    `/api/simulation/generate-persona-from-ai`,
    params,
  );
  return response;
};

////// PRICING //////
export const getPricingPlans = async () => {
  setAuthToken();
  const response = await Axios.get("/api/pricing/plans");
  return response;
};

export const getPricingModels = async () => {
  setAuthToken();
  const response = await Axios.get("/api/pricing/models");
  return response;
};

export const getPricingMeterRates = async () => {
  setAuthToken();
  const response = await Axios.get("/api/pricing/meter-rates");
  return response;
};

export const getPricingEditHistory = async () => {
  setAuthToken();
  const response = await Axios.get("/api/pricing/edit-history");
  return response;
};

export const modifyPricingDetails = async (body) => {
  setAuthToken();
  const response = await Axios.post("/api/pricing/modify-details", body);
  return response;
};

export const getPricingContracts = async (params = {}) => {
  setAuthToken();
  const response = await Axios.get("/api/pricing/contracts", { params });
  return response;
};

export const getPricingChangeHistory = async (params = {}) => {
  setAuthToken();
  const response = await Axios.get("/api/pricing/logs/change-history", { params });
  return response;
};

export const getPricingCreditBalance = async (params = {}) => {
  setAuthToken();
  const response = await Axios.get("/api/pricing/logs/credit-balance", { params });
  return response;
};

export const getPricingCreditUsage = async (params = {}) => {
  setAuthToken();
  const response = await Axios.get("/api/pricing/logs/credit-usage", { params });
  return response;
};

export const getPricingCreditLedger = async (params = {}) => {
  setAuthToken();
  const response = await Axios.get("/api/pricing/logs/credit-ledger", { params });
  return response;
};

export const downloadPricingCreditLedger = async (params = {}) => {
  setAuthToken();
  const response = await Axios.get('/api/pricing/logs/credit-ledger/download', {
    responseType: 'blob',
    params,
  });
  return response;
};

// System configuration (key/value store)
export const getPricingSystemConfiguration = async (params = {}) => {
  setAuthToken();
  const response = await Axios.get("/api/pricing/system-configuration", { params });
  return response;
};

export const postPricingSystemConfiguration = async (body) => {
  setAuthToken();
  const response = await Axios.post("/api/pricing/system-configuration", body);
  return response;
};

export const getPricingUsageReports = async (params = {}) => {
  setAuthToken();
  const response = await Axios.get('/api/pricing/usage/reports', { params });
  return response;
};

export const getPricingStatements = async (params = {}) => {
  setAuthToken();
  const response = await Axios.get('/api/pricing/statements', { params });
  return response;
};

export const getPricingConfigChangeLogs = async (params = {}) => {
  setAuthToken();
  const response = await Axios.get('/api/pricing/logs/config-changes', { params });
  return response;
};

export const downloadPricingConfigChangeLogs = async () => {
  setAuthToken();
  const response = await Axios.get('/api/pricing/logs/config-changes/download', {
    responseType: 'blob',
  });
  return response;
};

export const downloadPricingStatement = async (agencyId, period = {}) => {
  setAuthToken();
  const month = period?.month ?? null;
  const year = period?.year ?? null;
  const response = await Axios.get(`/api/pricing/statements/pdf`, {
    responseType: 'blob',
    params: { agency_id: agencyId, month, year },
  });
  return response;
};

export const createPricingContract = async (body) => {
  setAuthToken();
  const response = await Axios.post("/api/pricing/contracts/create", body);
  return response;
};

// INV-12: delete a single contract (super admin; only without usage and without a sent invoice)
// #2: dryRun only checks and returns what would not be invoiced (for the confirm dialog)
export const deletePricingContract = async (contractId, { dryRun = false } = {}) => {
  setAuthToken();
  const response = await Axios.delete("/api/pricing/contracts", {
    data: { contract_id: Number(contractId), ...(dryRun ? { dry_run: true } : {}) },
  });
  return response;
};

export const overridePricingContract = async (body) => {
  setAuthToken();
  const response = await Axios.post("/api/pricing/contracts/override", body);
  return response;
};

export const makePricingAdjustments = async (body) => {
  setAuthToken();
  const response = await Axios.post("/api/pricing/contracts/adjust", body);
  return response;
};

export const toggleContractRenewal = async (body) => {
  setAuthToken();
  const response = await Axios.post("/api/pricing/contracts/toggle-renewal", body);
  return response;
};

export const updateOwnContractBillingEmail = async (body) => {
  setAuthToken();
  const response = await Axios.post("/api/pricing/contracts/billing-email", body);
  return response;
};

export const toggleOwnContractRenewal = async (body) => {
  setAuthToken();
  const response = await Axios.post("/api/pricing/contracts/toggle-renewal-self", body);
  return response;
};

// Agency admin self-service: "Start a new period today" after a PO block
export const restartOwnContract = async () => {
  setAuthToken();
  const response = await Axios.post("/api/pricing/contracts/restart-self");
  return response;
};

// Agency admin self-service: "Start a new contract" after the cancelled one ended
export const startOwnNewContract = async ({ tier, cycle }) => {
  setAuthToken();
  const response = await Axios.post("/api/pricing/contracts/new-self", { tier, cycle });
  return response;
};

// Super admin: "Start a new period today" for an agency paused for a missing PO
export const restartContractAfterPoBlock = async (agencyId) => {
  setAuthToken();
  const response = await Axios.post("/api/pricing/contracts/restart", { agency_id: Number(agencyId) });
  return response;
};

// Super admin: "Start a new contract" for an agency whose cancelled contract ended
export const startNewContractAfterCancel = async ({ agencyId, tier, cycle }) => {
  setAuthToken();
  const response = await Axios.post("/api/pricing/contracts/new-after-cancel", {
    agency_id: Number(agencyId),
    tier,
    cycle,
  });
  return response;
};

export const getOwnContractHistory = async (params = {}) => {
  setAuthToken();
  const response = await Axios.get("/api/pricing/contracts/history", { params });
  return response;
};
